import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FileDown, ArrowLeft } from 'lucide-react';
import PublicAnalyticsBeacon from '../components/PublicAnalyticsBeacon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public Terms & Conditions viewer.
 *
 * Mounted at /p/:token/terms — accessible without authentication. Renders
 * the contract terms (per-proposal override, else the company default) as
 * styled HTML and offers a "Download as PDF" button. `?print=1` strips
 * navigation chrome so the headless-Chrome PDF renderer captures only the
 * terms.
 */
export default function PublicTerms() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const isPrint = searchParams.get('print') === '1';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const { data: d } = await axios.get(`${API}/public/proposals/${token}/terms`);
                if (!live) return;
                setData(d);
            } catch (e) {
                if (!live) return;
                setErr(e?.response?.data?.detail || 'Could not load terms.');
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => { live = false; };
    }, [token]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-neutral-400 text-sm">Loading terms…</div>;
    }
    if (err) {
        return <div className="min-h-screen flex items-center justify-center text-rose-500 text-sm">{err}</div>;
    }
    const termsHtml = (data?.terms_html || '').trim();
    if (!termsHtml) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-3">
                <p className="text-neutral-700 text-base">No terms &amp; conditions have been set for this proposal.</p>
                <Link to={`/p/${token}/narrative`} className="text-[#002FA7] text-sm underline">← Back to proposal</Link>
            </div>
        );
    }

    const brand = data?.brand_primary || '#0B3C5D';
    const accent = data?.brand_accent || '#3BC6B4';
    const themeStyle = { '--tc-brand': brand, '--tc-accent': accent };
    const pdfUrl = `${API}/public/proposals/${token}/terms-pdf`;

    return (
        <div
            className={`tc-root min-h-screen ${isPrint ? 'tc-print' : ''}`}
            style={themeStyle}
            data-testid="public-terms-page"
            data-print={isPrint ? '1' : '0'}
        >
            <style>{TERMS_CSS}</style>
            {!isPrint && <PublicAnalyticsBeacon token={token} viewKind="terms" />}

            {!isPrint && (
                <header className="tc-header">
                    <div className="tc-header-inner">
                        <div className="tc-brand">
                            {data?.company_logo_url
                                ? <img src={data.company_logo_url} alt={data.company_name} className="tc-logo" />
                                : <span className="tc-brand-name">{data?.company_name || 'Beyond Measure'}</span>}
                        </div>
                        <div className="tc-header-actions">
                            <Link
                                to={`/p/${token}/narrative`}
                                className="tc-back"
                                data-testid="public-terms-back-link"
                            >
                                <ArrowLeft size={14} /> Back to proposal
                            </Link>
                            <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tc-download"
                                data-testid="public-terms-download-pdf"
                            >
                                <FileDown size={15} />
                                Download as PDF
                            </a>
                        </div>
                    </div>
                </header>
            )}

            <main className="tc-main">
                <div className="tc-container">
                    <div className="tc-meta-strip" data-testid="public-terms-meta">
                        <span className="tc-meta-eyebrow">Terms &amp; Conditions</span>
                        <h1 className="tc-title">
                            {data?.company_name || 'Beyond Measure'} — Master Services Agreement
                        </h1>
                        <div className="tc-meta-row">
                            {data?.client_name && (
                                <div className="tc-meta-item">
                                    <span className="tc-meta-label">Prepared for</span>
                                    <span className="tc-meta-value">{data.client_name}</span>
                                </div>
                            )}
                            {data?.proposal_number && (
                                <div className="tc-meta-item">
                                    <span className="tc-meta-label">Proposal</span>
                                    <span className="tc-meta-value">{data.proposal_number}</span>
                                </div>
                            )}
                            {data?.is_override && (
                                <div className="tc-meta-item">
                                    <span className="tc-meta-label">Note</span>
                                    <span className="tc-meta-value tc-meta-flag">Custom terms for this proposal</span>
                                </div>
                            )}
                        </div>
                        <p className="tc-intro">
                            Please read these Terms &amp; Conditions carefully. By signing the proposal you confirm you have read,
                            understood and accept the terms below. You can download a PDF copy at any time using the button above.
                        </p>
                    </div>

                    <article
                        className="tc-body"
                        data-testid="public-terms-content"
                        dangerouslySetInnerHTML={{ __html: termsHtml }}
                    />

                    {!isPrint && (
                        <div className="tc-footer-actions">
                            <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tc-download"
                                data-testid="public-terms-download-pdf-bottom"
                            >
                                <FileDown size={15} />
                                Download as PDF
                            </a>
                            <Link
                                to={`/p/${token}/narrative`}
                                className="tc-back"
                                data-testid="public-terms-back-link-bottom"
                            >
                                <ArrowLeft size={14} /> Back to proposal &amp; sign
                            </Link>
                        </div>
                    )}
                </div>
            </main>

            {!isPrint && (
                <footer className="tc-foot">
                    <div className="tc-container tc-foot-inner">
                        <div className="tc-foot-brand">{data?.company_name || 'Beyond Measure'}</div>
                        {data?.company_address && <div className="tc-foot-addr">{data.company_address}</div>}
                    </div>
                </footer>
            )}
        </div>
    );
}

const TERMS_CSS = `
.tc-root {
    --tc-bg: #fafaf7;
    --tc-fg: #1a1a1a;
    --tc-muted: #5b6470;
    --tc-border: #e7e5dc;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: var(--tc-bg);
    color: var(--tc-fg);
    line-height: 1.6;
}
.tc-container { max-width: 880px; margin: 0 auto; padding: 0 32px; }

.tc-header {
    background: #fff;
    border-bottom: 1px solid var(--tc-border);
    padding: 16px 0;
    position: sticky;
    top: 0;
    z-index: 5;
}
.tc-header-inner {
    max-width: 880px;
    margin: 0 auto;
    padding: 0 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
}
.tc-logo { height: 32px; width: auto; }
.tc-brand-name {
    font-weight: 700;
    color: var(--tc-brand);
    font-size: 17px;
    letter-spacing: -0.01em;
}
.tc-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.tc-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 14px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--tc-fg);
    text-decoration: none;
    border: 1px solid var(--tc-border);
    background: #fff;
    transition: border-color .15s, background .15s;
}
.tc-back:hover { border-color: var(--tc-brand); background: var(--tc-bg); }
.tc-download {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: var(--tc-brand);
    color: #fff !important;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
    border: 2px solid var(--tc-brand);
    transition: background .15s, transform .12s, box-shadow .15s;
}
.tc-download:hover {
    background: color-mix(in srgb, var(--tc-brand) 88%, #000);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px -6px rgba(0,47,167,0.4);
}

.tc-main { padding: 56px 0 80px; }
.tc-meta-strip {
    padding-bottom: 32px;
    border-bottom: 1px solid var(--tc-border);
    margin-bottom: 36px;
}
.tc-meta-eyebrow {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: var(--tc-brand);
    font-weight: 700;
}
.tc-title {
    font-size: clamp(26px, 3.4vw, 34px);
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--tc-fg);
    line-height: 1.15;
    margin: 10px 0 22px;
}
.tc-meta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    border-top: 1px solid var(--tc-border);
    border-left: 1px solid var(--tc-border);
}
.tc-meta-item {
    flex: 1 1 200px;
    background: #fff;
    padding: 12px 16px;
    border-right: 1px solid var(--tc-border);
    border-bottom: 1px solid var(--tc-border);
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.tc-meta-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--tc-muted);
    font-weight: 600;
}
.tc-meta-value { font-size: 13.5px; font-weight: 600; color: var(--tc-fg); }
.tc-meta-flag { color: var(--tc-accent); }
.tc-intro {
    margin: 20px 0 0;
    font-size: 14px;
    color: var(--tc-muted);
    line-height: 1.6;
}

.tc-body {
    font-size: 14.5px;
    line-height: 1.7;
    color: var(--tc-fg);
}
.tc-body h1 {
    font-size: 17px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tc-brand);
    margin: 36px 0 14px;
    padding-bottom: 6px;
    border-bottom: 2px solid var(--tc-brand);
}
.tc-body h1:first-child { margin-top: 0; }
.tc-body h2 {
    font-size: 15px;
    font-weight: 700;
    margin: 22px 0 6px;
    color: var(--tc-fg);
    letter-spacing: -0.005em;
}
.tc-body h3 {
    font-size: 14px;
    font-weight: 700;
    margin: 16px 0 6px;
    color: var(--tc-fg);
}
.tc-body p { margin: 0 0 12px; }
.tc-body ul, .tc-body ol { margin: 0 0 14px; padding-left: 22px; }
.tc-body li { margin: 4px 0; }
.tc-body strong { font-weight: 700; color: var(--tc-fg); }
.tc-body u { text-decoration: underline; }
.tc-body em { font-style: italic; }
.tc-body blockquote {
    border-left: 3px solid var(--tc-accent);
    padding: 8px 16px;
    color: var(--tc-muted);
    margin: 12px 0;
}
.tc-body hr {
    border: none;
    border-top: 1px solid var(--tc-border);
    margin: 24px 0;
}
.tc-body a {
    color: var(--tc-brand);
    text-decoration: underline;
}

.tc-footer-actions {
    margin-top: 48px;
    padding-top: 32px;
    border-top: 1px solid var(--tc-border);
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
}

.tc-foot {
    background: var(--tc-fg);
    color: #fff;
    padding: 28px 0;
    margin-top: 0;
}
.tc-foot-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}
.tc-foot-brand { font-weight: 700; font-size: 14px; letter-spacing: -0.01em; }
.tc-foot-addr { font-size: 12.5px; color: rgba(255,255,255,0.6); }

/* Print mode */
.tc-root.tc-print .tc-header,
.tc-root.tc-print .tc-foot,
.tc-root.tc-print .tc-footer-actions { display: none; }
.tc-root.tc-print .tc-main { padding: 28px 0 28px; }
.tc-root.tc-print .tc-meta-strip { padding-bottom: 18px; margin-bottom: 22px; }
.tc-root.tc-print .tc-title { font-size: 22px; margin: 6px 0 14px; }
.tc-root.tc-print .tc-body { font-size: 12.5px; line-height: 1.55; }
.tc-root.tc-print .tc-body h1 { font-size: 14px; margin: 22px 0 8px; padding-bottom: 4px; }
.tc-root.tc-print .tc-body h2 { font-size: 12.5px; margin: 14px 0 4px; }
.tc-root.tc-print .tc-body h3 { font-size: 12px; margin: 10px 0 4px; }
.tc-root.tc-print .tc-body p { margin: 0 0 8px; }
.tc-root.tc-print .tc-body ul,
.tc-root.tc-print .tc-body ol { margin: 0 0 8px; }

@media print {
    .tc-header, .tc-foot, .tc-footer-actions { display: none !important; }
}

@media (max-width: 640px) {
    .tc-header-inner { flex-direction: column; align-items: flex-start; }
    .tc-main { padding: 36px 0 56px; }
    .tc-container, .tc-header-inner { padding: 0 20px; }
}
`;
